import type {
  AdminAdjustInput,
  AdminAuditRow,
  AdminJobDetail,
  AdminJobRow,
  AdminPage,
  AdminPriceRow,
  AdminProjectRow,
  AdminProviderCheck,
  AdminProviderRow,
  AdminReferralRow,
  AdminUserDetail,
  AdminUsageRow,
  AdminUserRow,
} from '@alterium/shared';
import { api } from './client';

export interface AdminOverview {
  activeJobs: number;
  queuedJobs: number;
  failedLastDay: number;
  completedLastDay: number;
  failureRatePercent: number;
  providerCostCentsLastDay: number;
  unitsSpentLastDay: number;
  users: number;
  newUsersLastDay: number;
  providersDown: number;
  walletMismatches: number;
  queues: Array<{
    kind: string;
    pending: number;
    failedLastDay: number;
    avgSeconds: number | null;
  }>;
}

type Query = Record<string, string | number | undefined>;

const withQuery = (path: string, query: Query = {}): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const suffix = search.toString();
  return suffix ? `${path}?${suffix}` : path;
};

export const adminApi = {
  overview: () => api.get<AdminOverview>('/admin/overview'),
  /** §51: чем и на сколько работали. */
  usage: (days: number) => api.get<AdminUsageRow[]>(`/admin/usage?days=${days}`),

  users: (query: Query) => api.get<AdminPage<AdminUserRow>>(withQuery('/admin/users', query)),
  user: (id: string) => api.get<AdminUserDetail>(`/admin/users/${id}`),
  adjustUnits: (id: string, input: AdminAdjustInput) =>
    api.post<{ available: number; reserved: number }>(`/admin/users/${id}/units`, input),
  /** §41: служебному аккаунту видна техническая изнанка его роликов. */
  setService: (id: string, isService: boolean) =>
    api.post<{ id: string; email: string; isService: boolean }>(`/admin/users/${id}/service`, {
      isService,
    }),
  setStatus: (id: string, status: 'active' | 'blocked', reason?: string) =>
    api.post<AdminUserDetail>(`/admin/users/${id}/status`, { status, reason }),

  projects: (query: Query) =>
    api.get<AdminPage<AdminProjectRow>>(withQuery('/admin/projects', query)),

  jobs: (query: Query) => api.get<AdminPage<AdminJobRow>>(withQuery('/admin/jobs', query)),
  job: (id: string) => api.get<AdminJobDetail>(`/admin/jobs/${id}`),
  refundJob: (id: string, reason: string) =>
    api.post<{ units: number }>(`/admin/jobs/${id}/refund`, { reason }),

  providers: () => api.get<AdminProviderRow[]>('/admin/providers'),
  updateProvider: (id: string, input: Record<string, unknown>) =>
    api.patch<AdminProviderRow>(`/admin/providers/${id}`, input),
  updateCredentials: (id: string, input: Record<string, string>) =>
    api.put<AdminProviderRow>(`/admin/providers/${id}/credentials`, input),
  checkProvider: (id: string) => api.post<AdminProviderCheck>(`/admin/providers/${id}/check`, {}),

  prices: () => api.get<AdminPriceRow[]>('/admin/prices'),
  updatePrice: (code: string, units: number, reason?: string) =>
    api.patch<AdminPriceRow>(`/admin/prices/${code}`, { units, reason }),

  referrals: (query: Query) =>
    api.get<AdminPage<AdminReferralRow>>(withQuery('/admin/referrals', query)),
  resolveReferral: (id: string, action: 'approve' | 'revoke', reason?: string) =>
    api.post<{ success: boolean }>(`/admin/referrals/${id}`, { action, reason }),

  audit: (query: Query) => api.get<AdminPage<AdminAuditRow>>(withQuery('/admin/audit', query)),
};
